import {
  activeFilterChips,
  buildCategoryUrl,
  buildClearAllUrl,
  buildFilterUrl,
  clearAllFilterState,
  clearFacetState,
  createFilterState,
  facetOptionSelected,
  filterStatesEqual,
  hasActiveFilters,
  normalizeFilterState,
  removeFilterChip,
  selectedCountForFacet,
  setFacetOption,
  setFilterValue,
  validatePriceFacets,
} from "./pancha-direct-filter-core.js?v=20260811-1";
import { buildPanchaFilterFacets } from "./pancha-direct-filter-config.js?v=20261018-1";
import { filterCopy, filterLanguage } from "./pancha-direct-filter-i18n.js?v=20261018-1";

const UPDATE_EVENT = "pancha-direct-filter:update";
const MOBILE_QUERY = "(max-width: 1024px)";
let instanceCount = 0;

function createElement(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

function parseJsonElement(root, selector, fallback) {
  const node = root.querySelector(selector);
  if (!node) return fallback;
  return JSON.parse(node.textContent ?? "null");
}

function formatNumber(value, locale) {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value ?? "");
  return new Intl.NumberFormat(filterLanguage(locale) === "en" ? "en-US" : "ja-JP", { maximumFractionDigits: 2 }).format(number);
}

function priceChipLabel(chip, locale) {
  const symbol = chip.currencySymbol ?? "¥";
  const min = chip.min === "" ? "0" : formatNumber(chip.min, locale);
  const max = chip.max === "" ? formatNumber(chip.rangeMax, locale) : formatNumber(chip.max, locale);
  return `${chip.title}: ${symbol}${min}${filterCopy(locale, "rangeSeparator")}${symbol}${max}`;
}

function visibleFocusableElements(container) {
  const selector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  return [...container.querySelectorAll(selector)].filter(
    (element) => element.tabIndex >= 0 && !element.matches(":disabled") &&
      !element.closest("[hidden], [inert]") && element.getClientRects().length > 0 &&
      window.getComputedStyle(element).visibility === "visible",
  );
}

class PanchaDirectFilter extends HTMLElement {
  constructor() {
    super();
    this._instanceId = `pancha-direct-filter-${++instanceCount}`;
    this._initialized = false;
    this._clearAllRequested = false;
    this._drawerWasOpen = false;
    this._opener = null;
    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
    this._onPageState = this._onPageState.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;

    try {
      const nativeFacets = parseJsonElement(
        this,
        "[data-pancha-native-filters]",
        [],
      );
      this._context = parseJsonElement(
        this,
        "[data-pancha-filter-context]",
        null,
      );
      if (!this._context?.baseCollectionUrl) throw new Error("missing context");
      this._locale = this._context.locale || document.documentElement.lang || "ja";
      this._facets = buildPanchaFilterFacets(nativeFacets, { ...this._context, locale: this._locale });
    } catch (_error) {
      this.removeAttribute("data-ready");
      this.hidden = true;
      return;
    }

    if (this._facets.length === 0) {
      this.removeAttribute("data-ready");
      this.hidden = true;
      return;
    }

    this._groups = this.querySelector("[data-pancha-filter-groups]");
    this._status = this.querySelector("[data-pancha-filter-status]");
    this._applyButton = this.querySelector("[data-pancha-apply]");
    this._clearAllButton = this.querySelector("[data-pancha-clear-all]");
    this._drawer = document.getElementById(this.dataset.drawerId || "halo-sidebar");
    this._mobileMedia = window.matchMedia(MOBILE_QUERY);
    this._applied = createFilterState(
      this._facets,
      window.location.href,
      this._context,
    );
    this._pending = normalizeFilterState(this._applied);
    this._hasFilterQuery = [...new URL(window.location.href).searchParams.keys()]
      .some((name) => name.startsWith("filter.") || name.startsWith("pancha_"));

    this.setAttribute("role", "region");
    this.setAttribute("aria-label", this.text("filter"));
    this._applyButton.textContent = this.text("apply");
    this._clearAllButton.textContent = this.text("clearAll");
    this._renderGroups();
    this._syncControls();
    this._enhanceTriggers();

    this.addEventListener("click", this._onClick);
    this.addEventListener("change", this._onChange);
    this.addEventListener("input", this._onInput);
    document.addEventListener("click", this._onDocumentClick, true);
    document.addEventListener("keydown", this._onDocumentKeydown);
    window.addEventListener("pageshow", this._onPageState);
    window.addEventListener("popstate", this._onPageState);

    this._drawerWasOpen = document.body.classList.contains("open-mobile-sidebar");
    this._bodyObserver = new MutationObserver(() => this._syncDrawerState());
    this._bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    this._initialized = true;
    this._notifyToolbar();
  }

  disconnectedCallback() {
    if (!this._initialized) return;
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("change", this._onChange);
    this.removeEventListener("input", this._onInput);
    document.removeEventListener("click", this._onDocumentClick, true);
    document.removeEventListener("keydown", this._onDocumentKeydown);
    window.removeEventListener("pageshow", this._onPageState);
    window.removeEventListener("popstate", this._onPageState);
    this._bodyObserver?.disconnect();
    this._initialized = false;
  }

  text(key, variables) {
    return filterCopy(this._locale, key, variables);
  }

  _renderGroups() {
    this._groups.replaceChildren();

    for (const facet of this._facets) {
      const group = createElement("section", "pancha-direct-filter__group");
      const headingId = `${this._instanceId}-${facet.id}-heading`;
      group.dataset.panchaFacetId = facet.id;
      group.setAttribute("aria-labelledby", headingId);

      const heading = createElement("h2", "pancha-direct-filter__heading");
      heading.id = headingId;
      const headingCopy = createElement("span", "pancha-direct-filter__heading-copy");
      headingCopy.append(createElement("span", "", facet.title));

      if (facet.type !== "collection") {
        const count = createElement("span", "pancha-direct-filter__count");
        count.dataset.panchaFacetCount = facet.id;
        count.hidden = true;
        headingCopy.append(count);
      }

      heading.append(headingCopy);
      group.append(heading);

      const panel = createElement("div", "pancha-direct-filter__panel");
      if (facet.type === "collection") {
        panel.append(this._renderCollectionFacet(facet));
      } else if (facet.type === "price_range" || facet.type === "price_points") {
        panel.append(this._renderPriceFacet(facet));
      } else {
        panel.append(this._renderListFacet(facet));
      }

      if (facet.type !== "collection") {
        const clearButton = createElement(
          "button",
          "pancha-direct-filter__group-clear",
          this.text("clearFacet", { title: facet.title }),
        );
        clearButton.type = "button";
        clearButton.dataset.panchaClearFacet = facet.id;
        panel.append(clearButton);
      }

      group.append(panel);
      this._groups.append(group);
    }
  }

  _renderCollectionFacet(facet) {
    const list = createElement("ul", "pancha-direct-filter__list");
    list.setAttribute("role", "list");

    for (const value of facet.values) {
      const item = createElement("li", "pancha-direct-filter__option");
      const link = createElement(
        "a",
        "pancha-direct-filter__collection-link",
      );
      link.href = buildCategoryUrl(window.location.href, value.url).toString();
      const marker = createElement("span", "pancha-direct-filter__collection-marker");
      marker.setAttribute("aria-hidden", "true");
      link.append(
        marker,
        createElement("span", "pancha-direct-filter__collection-label", value.label),
      );
      if (value.active) {
        link.classList.add("is-active");
        link.setAttribute("aria-current", "page");
      }
      item.append(link);
      list.append(item);
    }

    return list;
  }

  _renderListFacet(facet) {
    const list = createElement("ul", "pancha-direct-filter__list");
    list.setAttribute("role", "list");

    facet.values.forEach((value, index) => {
      const item = createElement("li", "pancha-direct-filter__option");
      const label = createElement("label", "pancha-direct-filter__option-label");
      const input = createElement("input", "pancha-direct-filter__checkbox");
      const inputId = `${this._instanceId}-${facet.id}-${index}`;
      const count = Number(value.count);
      const hasCount = Number.isFinite(count);
      const disabled = hasCount && count === 0 && !value.active;
      const isColor = facet.key === "color" && Boolean(value.swatch);

      input.type = facet.multiple ? "checkbox" : "radio";
      input.id = inputId;
      input.name = facet.type === "tag_path"
        ? `${this._instanceId}-${facet.id}`
        : facet.paramName;
      input.value = String(value.key ?? value.value);
      input.dataset.panchaFacetId = facet.id;
      input.dataset.panchaOptionIndex = String(index);
      input.disabled = disabled;
      label.htmlFor = inputId;
      if (disabled) label.classList.add("is-disabled");
      if (isColor) label.classList.add("pancha-direct-filter__option-label--color");

      label.append(input);
      if (isColor) {
        const swatch = createElement("span", "pancha-direct-filter__color-swatch");
        swatch.setAttribute("aria-hidden", "true");
        swatch.style.setProperty("--pancha-filter-swatch", value.swatch);
        label.append(swatch);
      }
      label.append(createElement("span", "", value.label));
      if (hasCount) {
        label.append(
          createElement(
            "span",
            "pancha-direct-filter__value-count",
            `(${count})`,
          ),
        );
      }
      item.append(label);
      list.append(item);
    });

    return list;
  }

  _renderPriceFacet(facet) {
    const wrapper = createElement("div", "pancha-direct-filter__price");
    const minParam = facet.type === "price_points"
      ? facet.minStateParam
      : facet.minParamName;
    const maxParam = facet.type === "price_points"
      ? facet.maxStateParam
      : facet.maxParamName;
    wrapper.append(
      this._renderPriceInput(facet, "min", minParam, this.text("minimum")),
    );
    wrapper.append(createElement("span", "pancha-direct-filter__price-separator", this.text("rangeSeparator")));
    wrapper.append(
      this._renderPriceInput(facet, "max", maxParam, this.text("maximum")),
    );
    return wrapper;
  }

  _renderPriceInput(facet, boundary, paramName, labelText) {
    const label = createElement("label", "pancha-direct-filter__price-field");
    const labelCopy = createElement("span", "", labelText);
    const inputWrap = createElement("span", "pancha-direct-filter__price-input-wrap");
    const symbol = createElement(
      "span",
      "pancha-direct-filter__currency",
      facet.currencySymbol || "¥",
    );
    const input = createElement("input", "pancha-direct-filter__price-input");

    input.type = "number";
    input.inputMode = "decimal";
    input.min = "0";
    input.step = "1";
    if (facet.rangeMax !== "") input.max = String(facet.rangeMax);
    input.name = paramName;
    input.dataset.panchaPrice = boundary;
    input.dataset.panchaFacetId = facet.id;
    input.setAttribute("aria-label", labelText);
    inputWrap.append(symbol, input);
    label.append(labelCopy, inputWrap);
    return label;
  }

  _onChange(event) {
    const input = event.target.closest(".pancha-direct-filter__checkbox");
    if (!input || !this.contains(input)) return;
    const facet = this._facets.find(
      (item) => item.id === input.dataset.panchaFacetId,
    );
    if (!facet) return;
    const option = facet.values?.[Number(input.dataset.panchaOptionIndex)];
    if (!option) return;

    this._pending = setFacetOption(
      this._pending,
      facet,
      option,
      input.checked,
    );
    this._clearAllRequested = false;
    this._setStatus("");
    this._syncControls();
  }

  _onInput(event) {
    const input = event.target.closest(".pancha-direct-filter__price-input");
    if (!input || !this.contains(input)) return;

    this._pending = setFilterValue(this._pending, input.name, input.value);
    this._clearAllRequested = false;
    this._setStatus("");
    this._syncControls({ skipPriceInputs: true });
  }

  _onClick(event) {
    const clearFacetButton = event.target.closest("[data-pancha-clear-facet]");
    if (clearFacetButton) {
      const facet = this._facets.find(
        (item) => item.id === clearFacetButton.dataset.panchaClearFacet,
      );
      if (!facet) return;
      this._pending = clearFacetState(this._pending, facet);
      this._clearAllRequested = false;
      this._setStatus(this.text("clearedFacet", { title: facet.title }));
      this._syncControls();
      return;
    }

    if (event.target.closest("[data-pancha-clear-all]")) {
      this._pending = clearAllFilterState();
      this._clearAllRequested = true;
      this._setStatus(this.text("clearAllPending"));
      this._syncControls();
      return;
    }

    if (event.target.closest("[data-pancha-apply]")) this._apply();
  }

  _apply() {
    const validation = validatePriceFacets(this._pending, this._facets);
    if (!validation.valid) {
      this._setStatus(this.text(validation.code) || this.text("invalidPrice"));
      this.querySelector(
        `.pancha-direct-filter__price-input[data-pancha-facet-id="${validation.facetId}"]`,
      )?.focus();
      return;
    }

    const baseUrl = this._context.baseCollectionUrl;
    const currentUrl = this._clearAllRequested
      ? buildClearAllUrl(window.location.href, baseUrl)
      : new URL(window.location.href);
    const nextUrl = buildFilterUrl(
      currentUrl,
      this._facets,
      this._pending,
      baseUrl,
    );
    window.location.assign(nextUrl.toString());
  }

  _syncControls({ skipPriceInputs = false } = {}) {
    const state = normalizeFilterState(this._pending);

    for (const input of this.querySelectorAll(".pancha-direct-filter__checkbox")) {
      const facet = this._facets.find(
        (item) => item.id === input.dataset.panchaFacetId,
      );
      const option = facet?.values?.[Number(input.dataset.panchaOptionIndex)];
      input.checked = Boolean(facet && option && facetOptionSelected(state, facet, option));
    }

    if (!skipPriceInputs) {
      for (const input of this.querySelectorAll(".pancha-direct-filter__price-input")) {
        input.value = state.params[input.name]?.[0] ?? "";
      }
    }

    for (const facet of this._facets) {
      if (facet.type === "collection") continue;
      const selectedCount = selectedCountForFacet(this._pending, facet);
      const count = this.querySelector(`[data-pancha-facet-count="${facet.id}"]`);
      const clearButton = this.querySelector(`[data-pancha-clear-facet="${facet.id}"]`);
      if (count) {
        count.textContent = String(selectedCount);
        count.hidden = selectedCount === 0;
      }
      if (clearButton) clearButton.disabled = selectedCount === 0;
    }

    const unchanged = filterStatesEqual(this._pending, this._applied);
    this._applyButton.disabled = unchanged && !this._clearAllRequested;
    this._clearAllButton.disabled =
      !hasActiveFilters(this._pending) &&
      !hasActiveFilters(this._applied) &&
      !this._hasFilterQuery;
  }

  _discardPending() {
    this._pending = normalizeFilterState(this._applied);
    this._clearAllRequested = false;
    this._setStatus("");
    this._syncControls();
  }

  _onDocumentClick(event) {
    const trigger = event.target.closest("toolbar-item [data-sidebar]");
    if (trigger) this._opener = trigger;
  }

  _onDocumentKeydown(event) {
    const trigger = event.target.closest?.("toolbar-item [data-sidebar]");
    if (trigger && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      trigger.click();
      return;
    }

    const drawerOpen = document.body.classList.contains("open-mobile-sidebar");
    if (drawerOpen && this._mobileMedia?.matches && event.key === "Escape") {
      event.preventDefault();
      document.body.classList.remove("open-mobile-sidebar");
      return;
    }

    if (drawerOpen && this._mobileMedia?.matches && event.key === "Tab" && this._drawer) {
      const focusable = visibleFocusableElements(this._drawer);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!focusable.includes(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

  }

  _syncDrawerState() {
    const drawerOpen = document.body.classList.contains("open-mobile-sidebar");
    if (drawerOpen === this._drawerWasOpen) return;

    this._drawerWasOpen = drawerOpen;
    this._enhanceTriggers(drawerOpen);

    if (drawerOpen && this._mobileMedia.matches) {
      this._discardPending();
      if (this._drawer) {
        this._drawer.setAttribute("role", "dialog");
        this._drawer.setAttribute("aria-modal", "true");
        this._drawer.setAttribute("aria-label", this.text("filter"));
        const focusDrawer = () => {
          if (!this.isConnected || !this._mobileMedia.matches ||
            !document.body.classList.contains("open-mobile-sidebar")) return;
          const closeButton = this._drawer.querySelector("[data-close-sidebar]");
          closeButton?.setAttribute("aria-label", this.text("closeFilter"));
          if (this._drawer.contains(document.activeElement)) return;
          const focusable = visibleFocusableElements(this._drawer);
          const target = focusable.includes(closeButton) ? closeButton : focusable[0];
          target?.focus({ preventScroll: true });
        };
        focusDrawer();
        window.requestAnimationFrame(focusDrawer);
      }
      return;
    }

    this._discardPending();
    if (this._drawer) {
      this._drawer.removeAttribute("role");
      this._drawer.removeAttribute("aria-modal");
      this._drawer.removeAttribute("aria-label");
    }
    if (this._opener?.isConnected) this._opener.focus();
  }

  _enhanceTriggers(expanded = document.body.classList.contains("open-mobile-sidebar")) {
    for (const trigger of document.querySelectorAll("toolbar-item [data-sidebar]")) {
      if (!trigger.matches("button, a")) {
        trigger.setAttribute("role", "button");
        trigger.setAttribute("tabindex", "0");
      }
      trigger.setAttribute("aria-controls", this.dataset.drawerId || "halo-sidebar");
      trigger.setAttribute("aria-expanded", String(expanded));
      if (!trigger.getAttribute("aria-label")) {
        trigger.setAttribute("aria-label", this.text("openFilter"));
      }
    }
  }

  _onPageState(event) {
    if (event?.type === "popstate" || (event?.type === "pageshow" && event.persisted)) {
      window.location.reload();
      return;
    }
    this._applied = createFilterState(
      this._facets,
      window.location.href,
      this._context,
    );
    this._pending = normalizeFilterState(this._applied);
    this._clearAllRequested = false;
    this._hasFilterQuery = [...new URL(window.location.href).searchParams.keys()]
      .some((name) => name.startsWith("filter.") || name.startsWith("pancha_"));
    this._syncControls();
    this._notifyToolbar();
  }

  _setStatus(message) {
    if (this._status) this._status.textContent = message;
  }

  _notifyToolbar() {
    window.dispatchEvent(new CustomEvent(UPDATE_EVENT));
  }

  activeChips() {
    return activeFilterChips(this._facets, this._applied);
  }

  removeChip(chip) {
    const nextState = removeFilterChip(this._applied, chip);
    const nextUrl = buildFilterUrl(
      window.location.href,
      this._facets,
      nextState,
      this._context.baseCollectionUrl,
    );
    window.location.assign(nextUrl.toString());
  }

  clearAppliedFilters() {
    const nextUrl = buildClearAllUrl(
      window.location.href,
      this._context.baseCollectionUrl,
    );
    window.location.assign(nextUrl.toString());
  }
}

class PanchaDirectFilterChips extends HTMLElement {
  constructor() {
    super();
    this._render = this._render.bind(this);
  }

  connectedCallback() {
    window.addEventListener(UPDATE_EVENT, this._render);
    this._render();
  }

  disconnectedCallback() {
    window.removeEventListener(UPDATE_EVENT, this._render);
  }

  _render() {
    const filter = document.querySelector('pancha-direct-filter[data-ready="true"]');
    const chips = filter?.activeChips?.() ?? [];
    this.replaceChildren();
    if (filter) this.setAttribute("aria-label", filter.text("activeFilters"));

    if (!filter || chips.length === 0) {
      this.hidden = true;
      return;
    }

    for (const chip of chips) {
      const label = chip.type === "price_range"
        ? priceChipLabel(chip, filter._locale)
        : `${chip.title}: ${chip.label}`;
      const button = createElement("button", "pancha-direct-filter__chip");
      button.type = "button";
      button.setAttribute("aria-label", filter.text("removeChip", { label }));
      button.append(createElement("span", "", label));
      button.append(createElement("span", "pancha-direct-filter__chip-mark", "×"));
      button.addEventListener("click", () => filter.removeChip(chip));
      this.append(button);
    }

    const clearButton = createElement(
      "button",
      "pancha-direct-filter__toolbar-clear",
      filter.text("clearAll"),
    );
    clearButton.type = "button";
    clearButton.addEventListener("click", () => filter.clearAppliedFilters());
    this.append(clearButton);
    this.hidden = false;
    filter._enhanceTriggers();
  }
}

if (!customElements.get("pancha-direct-filter")) {
  customElements.define("pancha-direct-filter", PanchaDirectFilter);
}

if (!customElements.get("pancha-direct-filter-chips")) {
  customElements.define("pancha-direct-filter-chips", PanchaDirectFilterChips);
}
